import React, { useState, useEffect, useMemo, useRef } from 'react';
import apiClient from '../../../services/apiClient';
import { 
  Plus, 
  Minus, 
  User, 
  Settings, 
  X,
  CheckCircle,
  AlertCircle,
  FileText,
  Award,
  Shield,
  Activity
} from 'lucide-react';
import clinicianService from '../services/clinicianService';

const AdminCliniciansPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin Clinicians';
  }, []);

  // State for real clinician data
  const [cliniciansData, setCliniciansData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Edit functionality state
  const [editingSection, setEditingSection] = useState(null);
  const [editingClinicianId, setEditingClinicianId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);

  // Add Clinician Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [addClinicianData, setAddClinicianData] = useState({
    firstname: '',
    lastname: '',
    clinician_address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: '',
    phone: '',
    email: '',
    clinician_phone_number: '',
    workforce_medical: false,
    general_medical: false,
    ecg: false,
    spirometry: false,
    serology: false,
    physiotherapy: false,
    hearing: false,
    accounts_email: '',
    hourly_rate: '',
    appt_rate: '',
    is_active: true,
    date_of_birth: '',
    gender: '',
    languages: '',
    license_number: '',
    years_experience: '',
    verification_status: 'pending'
  });
  const [addLoading, setAddLoading] = useState(false);

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState('');
  const [filterByCountry, setFilterByCountry] = useState('');
  const [filterByState, setFilterByState] = useState('');
  const [filterBySuburb, setFilterBySuburb] = useState('');
  const [filterBySpecialty, setFilterBySpecialty] = useState('');
  const [sortBy, setSortBy] = useState('');

  // Collapsible state
  const [expandedClinician, setExpandedClinician] = useState(null);

  // Clinic assignment state
  const [availableClinics, setAvailableClinics] = useState([]);
  const [clinicSearchTerm, setClinicSearchTerm] = useState('');

  // Toast notification state
  const [toasts, setToasts] = useState([]);

  // Dropdown states
  const [showIncludeInactiveDropdown, setShowIncludeInactiveDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showSuburbDropdown, setShowSuburbDropdown] = useState(false);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Refs for dropdowns
  const includeInactiveRef = useRef(null);
  const countryRef = useRef(null);
  const stateRef = useRef(null);
  const suburbRef = useRef(null);
  const specialtyRef = useRef(null);
  const sortRef = useRef(null);

  // Toast notification functions
  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Fetch clinicians from API
  const fetchClinicians = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // CORS test removed - actual API calls work fine

      // Check if user is authenticated
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('⚠️ No authentication token found');
        setError('Please log in to view clinicians. No authentication token found.');
        return;
      }

      // Try the clinician service
      try {
        console.log('👩‍⚕️ Attempting to fetch clinicians via clinicianService...');
        const data = await clinicianService.getAllClinicians();
        console.log('📊 Raw clinicians data from API:', data);
        
        if (!data || data.length === 0) {
          console.warn('⚠️ No clinicians found, this might be due to missing database migration');
          setError('No clinicians found. You may need to run the database migration first. Check the console for details.');
          return;
        }
        
        // Log each clinician's assigned clinic for debugging
        data.forEach((clinician, index) => {
          console.log(`👨‍⚕️ Clinician ${index + 1} (ID: ${clinician.id}):`, {
            name: `${clinician.firstname} ${clinician.lastname}`,
            assigned_clinic: clinician.assigned_clinic,
            clinic_id: clinician.assigned_clinic?.clinic_id
          });
        });
        
        setCliniciansData(data);
        console.log('✅ Successfully fetched clinicians via clinicianService');
        return;
      } catch (clinicianServiceError) {
        console.warn('⚠️ Clinician service failed:', clinicianServiceError);
        
        if (clinicianServiceError.message.includes('401') || clinicianServiceError.message.includes('Unauthorized')) {
          setError('Authentication failed. Please log in again.');
          return;
        } else {
          setError('Failed to load clinicians. Please check your connection and try again.');
        }
      }
    } catch (err) {
      setError('Failed to load clinicians');
      console.error('❌ Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available clinics
  const fetchAvailableClinics = async () => {
    try {
      console.log('🏥 Fetching available clinics...');
      const clinics = await clinicianService.getAvailableClinics();
      console.log('🏥 Available clinics received:', clinics);
      setAvailableClinics(clinics);
    } catch (error) {
      console.error('❌ Error fetching available clinics:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
      // Show error toast to help with debugging
      addToast(`Failed to load available clinics: ${error.message}`, 'error');
    }
  };

  // Load clinicians and clinics on component mount
  useEffect(() => {
    console.log('🔄 Component mounted, loading data...');
    fetchClinicians();
    fetchAvailableClinics();
  }, []);

  // Filtered and sorted clinicians
  const filteredClinicians = useMemo(() => {
    return cliniciansData.filter(clinician => {
      const matchesSearch = searchTerm === '' || 
        clinician.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinician.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinician.clinician_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinician.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinician.specialties?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesActive = includeInactive === '' || 
        (includeInactive === 'Yes' && clinician.is_active) ||
        (includeInactive === 'No' && !clinician.is_active);

      const matchesCountry = filterByCountry === '' || clinician.country === filterByCountry;
      const matchesState = filterByState === '' || clinician.state === filterByState;
      const matchesSuburb = filterBySuburb === '' || clinician.suburb === filterBySuburb;
      const matchesSpecialty = filterBySpecialty === '' || 
        clinician.specialties?.toLowerCase().includes(filterBySpecialty.toLowerCase());

      return matchesSearch && matchesActive && matchesCountry && matchesState && matchesSuburb && matchesSpecialty;
    }).sort((a, b) => {
      switch (sortBy) {
        case 'Name (A-Z)':
          return `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`);
        case 'Name (Z-A)':
          return `${b.firstname} ${b.lastname}`.localeCompare(`${a.firstname} ${a.lastname}`);
        case 'Specialty':
          return (a.specialties || '').localeCompare(b.specialties || '');
        case 'Date Added':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'Status':
          return (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0);
        default:
          return 0;
      }
    });
  }, [cliniciansData, searchTerm, includeInactive, filterByCountry, filterByState, filterBySuburb, filterBySpecialty, sortBy]);

  // Handle expanding/collapsing clinician details
  const toggleClinicianExpansion = (clinicianId) => {
    if (expandedClinician === clinicianId) {
      setExpandedClinician(null);
    } else {
      setExpandedClinician(clinicianId);
    }
    // Cancel any editing when expanding/collapsing
    cancelEdit();
  };

  // Edit functionality
  const startEdit = (section, clinicianId) => {
    const clinician = cliniciansData.find(c => c.id === clinicianId);
    if (clinician) {
      console.log('🔧 Starting edit for section:', section, 'clinician:', clinicianId);
      console.log('🔧 Full clinician object:', clinician);
      console.log('🔧 Assigned clinic object:', clinician.assigned_clinic);
      console.log('🔧 Available clinics array:', availableClinics);
      console.log('🔧 Available clinics length:', availableClinics.length);
      
      // If no available clinics, fetch them now
      if (availableClinics.length === 0) {
        console.log('🔧 No available clinics loaded, fetching now...');
        fetchAvailableClinics();
      }
      
      setEditingSection(section);
      setEditingClinicianId(clinicianId);
      const editData = prepareEditData(clinician, section);
      console.log('🔧 Prepared edit data:', editData);
      setEditFormData(editData);
    }
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditingClinicianId(null);
    setEditFormData({});
  };

  const prepareEditData = (clinician, section) => {
    switch (section) {
      case 'clinician-details':
        // Log clinician data for debugging
        console.log('🏥 Preparing edit data for clinician:', clinician.id);
        console.log('🏥 Full clinician object keys:', Object.keys(clinician));
        console.log('🏥 Assigned clinic data:', clinician.assigned_clinic);
        console.log('🏥 Assigned clinic type:', typeof clinician.assigned_clinic);
        console.log('🏥 Clinic ID from assigned_clinic:', clinician.assigned_clinic?.clinic_id);
        
        // Try different possible field names for clinic assignment
        const possibleClinicId = clinician.assigned_clinic?.clinic_id || 
                                clinician.assigned_clinic?.id || 
                                clinician.clinic_id || 
                                '';
        
        console.log('🏥 Final clinic ID to use:', possibleClinicId);
        
        return {
          firstname: clinician.firstname || '',
          lastname: clinician.lastname || '',
          clinician_address: clinician.clinician_address || '',
          suburb: clinician.suburb || '',
          state: clinician.state || '',
          postcode: clinician.postcode || '',
          country: clinician.country || '',
          phone: clinician.phone || '',
          email: clinician.email || '',
          clinician_phone_number: clinician.clinician_phone_number || '',
          assigned_clinic_id: possibleClinicId
        };
      case 'specialties':
        return {
          workforce_medical: clinician.workforce_medical || false,
          general_medical: clinician.general_medical || false,
          ecg: clinician.ecg || false,
          spirometry: clinician.spirometry || false,
          serology: clinician.serology || false,
          physiotherapy: clinician.physiotherapy || false,
          hearing: clinician.hearing || false
        };
      case 'accounts':
        return {
          accounts_email: clinician.accounts_email || '',
          hourly_rate: clinician.hourly_rate || '',
          appt_rate: clinician.appt_rate || ''
        };
      default:
        return {};
    }
  };

  const updateEditFormData = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveChanges = async () => {
    if (!editingClinicianId || !editingSection) return;

    try {
      setSaveLoading(true);
      
      console.log('💾 Saving changes for clinician:', editingClinicianId);
      console.log('💾 Edit form data:', editFormData);
      
      let updatedClinician;
      
      // Handle clinic assignment separately if it's in the edit data
      if (editFormData.assigned_clinic_id !== undefined) {
        const { assigned_clinic_id, ...otherData } = editFormData;
        
        console.log('💾 Clinic assignment ID:', assigned_clinic_id);
        console.log('💾 Other data:', otherData);
        
        // First update other clinician details (excluding clinic assignment)
        if (Object.keys(otherData).length > 0) {
          updatedClinician = await clinicianService.updateClinician(editingClinicianId, otherData);
        }
        
        // Handle clinic assignment
        const currentClinic = cliniciansData.find(c => c.id === editingClinicianId)?.assigned_clinic;
        const currentClinicId = currentClinic?.clinic_id;
        
        console.log('💾 Current clinic:', currentClinic);
        console.log('💾 Current clinic ID:', currentClinicId);
        console.log('💾 New clinic ID:', assigned_clinic_id);
        
        if (assigned_clinic_id && String(assigned_clinic_id) !== String(currentClinicId)) {
          // Assign new clinic (this will automatically remove any existing clinic assignment)
          console.log('💾 Assigning new clinic (old assignments will be automatically removed):', assigned_clinic_id);
          const clinicName = availableClinics.find(c => c.id == assigned_clinic_id)?.clinic_name || 'Unknown Clinic';
          console.log('💾 New clinic:', clinicName);
          await clinicianService.assignClinicToClinician(editingClinicianId, assigned_clinic_id);
          
          // Update local state with the new clinic assignment
          const newClinic = availableClinics.find(c => c.id == assigned_clinic_id);
          setCliniciansData(prevClinicians => 
            prevClinicians.map(clinician => 
              clinician.id === editingClinicianId 
                ? { 
                    ...clinician, 
                    assigned_clinic: newClinic ? {
                      clinic_id: newClinic.id,
                      clinic_name: newClinic.clinic_name,
                      clinic_address: newClinic.clinic_address,
                      suburb: newClinic.suburb,
                      state: newClinic.state
                    } : null
                  } 
                : clinician
            )
          );
        } else if (!assigned_clinic_id && currentClinicId) {
          // Remove clinic assignment (set to "No clinic assigned")
          console.log('💾 Removing all clinic assignments (setting to "No clinic assigned")');
          await clinicianService.removeClinicFromClinician(editingClinicianId, currentClinicId);
          
          // Update local state to remove clinic assignment
          setCliniciansData(prevClinicians => 
            prevClinicians.map(clinician => 
              clinician.id === editingClinicianId 
                ? { ...clinician, assigned_clinic: null }
                : clinician
            )
          );
        } else {
          console.log('💾 No clinic assignment change needed');
        }
      } else {
        // Normal update without clinic assignment
        console.log('💾 Normal update without clinic assignment');
        updatedClinician = await clinicianService.updateClinician(editingClinicianId, editFormData);
        
        // Update local state
        setCliniciansData(prevClinicians => 
          prevClinicians.map(clinician => 
            clinician.id === editingClinicianId ? { ...clinician, ...updatedClinician } : clinician
          )
        );
      }
      
      // Cancel edit mode
      cancelEdit();
      
      // Show success message
      addToast('Clinician details updated successfully!', 'success');
      
    } catch (error) {
      console.error('Error updating clinician:', error);
      addToast(`Failed to update clinician: ${error.message}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // Toggle active status
  const toggleClinicianStatus = async (clinicianId) => {
    try {
      const clinician = cliniciansData.find(c => c.id === clinicianId);
      if (!clinician) return;

      const newStatus = !clinician.is_active;
      const updatedClinician = await clinicianService.updateClinician(clinicianId, { is_active: newStatus });
      
      // Update local state
      setCliniciansData(prevClinicians => 
        prevClinicians.map(c => 
          c.id === clinicianId ? { ...c, ...updatedClinician } : c
        )
      );
      
      // Show success message
      addToast(`Clinician ${newStatus ? 'activated' : 'deactivated'} successfully!`, 'success');
      
    } catch (error) {
      console.error('Error updating clinician status:', error);
      addToast(`Failed to update clinician status: ${error.message}`, 'error');
    }
  };

  // Add clinician modal functions
  const openAddModal = () => {
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddClinicianData({
      firstname: '',
      lastname: '',
      clinician_address: '',
      suburb: '',
      state: '',
      postcode: '',
      country: '',
      phone: '',
      email: '',
      clinician_phone_number: '',
      workforce_medical: false,
      general_medical: false,
      ecg: false,
      spirometry: false,
      serology: false,
      physiotherapy: false,
      hearing: false,
      accounts_email: '',
      hourly_rate: '',
      appt_rate: '',
      is_active: true,
      date_of_birth: '',
      gender: '',
      languages: '',
      license_number: '',
      years_experience: '',
      verification_status: 'pending'
    });
  };

  const updateAddClinicianData = (field, value) => {
    setAddClinicianData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddClinician = async () => {
    try {
      setAddLoading(true);
      
      // Validate required fields
      if (!addClinicianData.email) {
        addToast('Email is required to create a clinician account.', 'error');
        return;
      }
      
      if (!addClinicianData.firstname || !addClinicianData.lastname) {
        addToast('First name and last name are required.', 'error');
        return;
      }
      
      // Create new clinician with user account via enhanced API
      const newClinician = await clinicianService.createClinicianWithUser(addClinicianData);
      
      // Add the new clinician to the local state
      setCliniciansData(prevClinicians => [newClinician, ...prevClinicians]);
      
      // Close modal and reset form
      closeAddModal();
      
      // Show success message
      addToast('Clinician created successfully! A user account has been created and credentials have been sent to the admin email.', 'success');
      
    } catch (error) {
      console.error('Error creating clinician:', error);
      
      // Check for specific error messages
      if (error.message.includes('User already exists with email')) {
        addToast('A user with this email already exists. Please use a different email address.', 'error');
      } else {
        addToast(`Failed to create clinician: ${error.message}`, 'error');
      }
    } finally {
      setAddLoading(false);
    }
  };

  // Dropdown handlers
  const toggleIncludeInactiveDropdown = () => {
    setShowIncludeInactiveDropdown(!showIncludeInactiveDropdown);
  };

  const toggleCountryDropdown = () => {
    setShowCountryDropdown(!showCountryDropdown);
  };

  const toggleStateDropdown = () => {
    setShowStateDropdown(!showStateDropdown);
  };

  const toggleSuburbDropdown = () => {
    setShowSuburbDropdown(!showSuburbDropdown);
  };

  const toggleSpecialtyDropdown = () => {
    setShowSpecialtyDropdown(!showSpecialtyDropdown);
  };

  const toggleSortDropdown = () => {
    setShowSortDropdown(!showSortDropdown);
  };

  // Selection handlers
  const handleIncludeInactiveSelect = (value) => {
    setIncludeInactive(value);
    setShowIncludeInactiveDropdown(false);
  };

  const handleCountrySelect = (country) => {
    setFilterByCountry(country);
    setShowCountryDropdown(false);
  };

  const handleStateSelect = (state) => {
    setFilterByState(state);
    setShowStateDropdown(false);
  };

  const handleSuburbSelect = (suburb) => {
    setFilterBySuburb(suburb);
    setShowSuburbDropdown(false);
  };

  const handleSpecialtySelect = (specialty) => {
    setFilterBySpecialty(specialty);
    setShowSpecialtyDropdown(false);
  };

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setShowSortDropdown(false);
  };

  // Filter reset functionality
  const resetFilters = () => {
    setSearchTerm('');
    setIncludeInactive('');
    setFilterByCountry('');
    setFilterByState('');
    setFilterBySuburb('');
    setFilterBySpecialty('');
    setSortBy('');
  };

  // Extract unique values for filter dropdowns
  const getUniqueCountries = useMemo(() => {
    return [...new Set(cliniciansData.map(c => c.country).filter(Boolean))];
  }, [cliniciansData]);

  const getUniqueStates = useMemo(() => {
    return [...new Set(cliniciansData.map(c => c.state).filter(Boolean))];
  }, [cliniciansData]);

  const getUniqueSuburbs = useMemo(() => {
    return [...new Set(cliniciansData.map(c => c.suburb).filter(Boolean))];
  }, [cliniciansData]);

  const getUniqueSpecialties = useMemo(() => {
    const specialties = new Set();
    cliniciansData.forEach(clinician => {
      if (clinician.specialties) {
        specialties.add(clinician.specialties);
      }
    });
    return [...specialties];
  }, [cliniciansData]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (includeInactiveRef.current && !includeInactiveRef.current.contains(event.target)) {
        setShowIncludeInactiveDropdown(false);
      }
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
      if (stateRef.current && !stateRef.current.contains(event.target)) {
        setShowStateDropdown(false);
      }
      if (suburbRef.current && !suburbRef.current.contains(event.target)) {
        setShowSuburbDropdown(false);
      }
      if (specialtyRef.current && !specialtyRef.current.contains(event.target)) {
        setShowSpecialtyDropdown(false);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Dummy qualifications data
  const getDummyQualifications = (clinicianId) => {
    const qualifications = [
      { id: 1, name: 'Nursing_Degree.pdf', type: 'Nursing Degree', icon: Award },
      { id: 2, name: 'Board_Certification.pdf', type: 'Board Certification', icon: Shield },
      { id: 3, name: 'Medical_License.pdf', type: 'Medical License', icon: FileText },
      { id: 4, name: 'Continuing_Education.pdf', type: 'Continuing Education', icon: FileText },
      { id: 5, name: 'Specialty_Certificate.pdf', type: 'Specialty Certificate', icon: Award }
    ];
    
    // Return 3-5 qualifications per clinician (varies by clinician ID)
    const count = 3 + (clinicianId % 3);
    return qualifications.slice(0, count);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading clinicians...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchClinicians}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`px-4 py-2 rounded-md shadow-lg text-white flex items-center space-x-2 ${
              toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <span className="text-sm">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-2 text-white hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Blue header */}
      <div className="bg-blue-600 text-white py-1 px-2">
        <h1 className="text-xl font-bold text-center">Clinicians</h1>
      </div>

      <div className="flex">
        {/* Left Column - Filters (25%) */}
        <div className="w-1/4 bg-gray-50 min-h-screen p-4">
          <div className="bg-white rounded-lg border border-blue-200 p-3 mt-3">
            <div className="space-y-3">
              {/* Add Clinician Button */}
              <div>
                <button
                  onClick={openAddModal}
                  className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Clinician
                </button>
              </div>

              {/* Search */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Search:
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Include Inactive */}
              <div className="relative" ref={includeInactiveRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Include Inactive:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleIncludeInactiveDropdown}
                >
                  <span className={includeInactive ? 'text-gray-900' : 'text-gray-500'}>
                    {includeInactive || 'Yes / No'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showIncludeInactiveDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {['Yes', 'No'].map((option) => (
                      <div
                        key={option}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleIncludeInactiveSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By Country */}
              <div className="relative" ref={countryRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Country:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleCountryDropdown}
                >
                  <span className={filterByCountry ? 'text-gray-900' : 'text-gray-500'}>
                    {filterByCountry || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showCountryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {getUniqueCountries.map((country) => (
                      <div
                        key={country}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleCountrySelect(country)}
                      >
                        {country}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By State */}
              <div className="relative" ref={stateRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By State:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleStateDropdown}
                >
                  <span className={filterByState ? 'text-gray-900' : 'text-gray-500'}>
                    {filterByState || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showStateDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {getUniqueStates.map((state) => (
                      <div
                        key={state}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleStateSelect(state)}
                      >
                        {state}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By Suburb */}
              <div className="relative" ref={suburbRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Suburb:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleSuburbDropdown}
                >
                  <span className={filterBySuburb ? 'text-gray-900' : 'text-gray-500'}>
                    {filterBySuburb || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSuburbDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {getUniqueSuburbs.map((suburb) => (
                      <div
                        key={suburb}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSuburbSelect(suburb)}
                      >
                        {suburb}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By Specialty */}
              <div className="relative" ref={specialtyRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Specialty:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleSpecialtyDropdown}
                >
                  <span className={filterBySpecialty ? 'text-gray-900' : 'text-gray-500'}>
                    {filterBySpecialty || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSpecialtyDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {getUniqueSpecialties.map((specialty) => (
                      <div
                        key={specialty}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSpecialtySelect(specialty)}
                      >
                        {specialty}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort By */}
              <div className="relative" ref={sortRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Sort By:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleSortDropdown}
                >
                  <span className={sortBy ? 'text-gray-900' : 'text-gray-500'}>
                    {sortBy || 'Sort Options'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSortDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {['Name (A-Z)', 'Name (Z-A)', 'Specialty', 'Date Added', 'Status'].map((option) => (
                      <div
                        key={option}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSortSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reset Filters Button */}
              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-xs flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Clinician List (75%) */}
        <div className="w-3/4 bg-gray-50 p-6">
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4">
              <div className="space-y-1">
                {filteredClinicians.map((clinician) => (
                  <div key={clinician.id} className="border border-gray-200 rounded-md">
                    <div className="flex justify-between items-center p-3">
                      <div className="flex items-center flex-1">
                        <User className="h-4 w-4 mr-2 text-blue-600" />
                      <span className="text-sm font-medium text-gray-900">
                          {clinician.firstname && clinician.lastname ? `${clinician.firstname} ${clinician.lastname}` : 'Unknown Clinician'}
                      </span>
                        {clinician.is_active === false && (
                          <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                            Inactive
                          </span>
                        )}
                    </div>
                    <button
                        onClick={() => toggleClinicianExpansion(clinician.id)}
                        className={`w-7 h-7 rounded-md transition-colors flex items-center justify-center ${
                          expandedClinician === clinician.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {expandedClinician === clinician.id ? (
                          <Minus className="h-3 w-3" />
                        ) : (
                      <Plus className="h-3 w-3" />
                        )}
                    </button>
                  </div>

                    {/* Expanded Clinician Details */}
                    {expandedClinician === clinician.id && (
                      <div className="bg-gray-100 p-4 space-y-4">
                        {/* Clinician Details Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedClinician === clinician.id 
                              ? 'bg-gradient-to-r from-blue-50 to-indigo-100 border-b border-blue-200 text-blue-900'
                              : 'bg-blue-600 text-white'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <User className="h-3 w-3 mr-2" />
                              Clinician Details
                            </h3>
                            {editingSection === 'clinician-details' && editingClinicianId === clinician.id ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={saveChanges}
                                  disabled={saveLoading}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {saveLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => toggleClinicianStatus(clinician.id)}
                                  className={`px-2 py-1 text-white rounded text-xs hover:opacity-80 ${
                                    clinician.is_active ? 'bg-green-600' : 'bg-red-600'
                                  }`}
                                >
                                  {clinician.is_active ? 'Active' : 'Inactive'}
                                </button>
                                <button
                                  onClick={() => startEdit('clinician-details', clinician.id)}
                                  className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                                >
                                  Edit Record
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            {/* First Row */}
                            <div className="grid grid-cols-5 gap-2 mb-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? editFormData.firstname || ''
                                      : clinician.firstname || ''
                                  }
                                  onChange={(e) => editingSection === 'clinician-details' && editingClinicianId === clinician.id && updateEditFormData('firstname', e.target.value)}
                                  readOnly={!(editingSection === 'clinician-details' && editingClinicianId === clinician.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? editFormData.lastname || ''
                                      : clinician.lastname || ''
                                  }
                                  onChange={(e) => editingSection === 'clinician-details' && editingClinicianId === clinician.id && updateEditFormData('lastname', e.target.value)}
                                  readOnly={!(editingSection === 'clinician-details' && editingClinicianId === clinician.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Clinician Address</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? editFormData.clinician_address || ''
                                      : clinician.clinician_address || ''
                                  }
                                  onChange={(e) => editingSection === 'clinician-details' && editingClinicianId === clinician.id && updateEditFormData('clinician_address', e.target.value)}
                                  readOnly={!(editingSection === 'clinician-details' && editingClinicianId === clinician.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Suburb</label>
                                {editingSection === 'clinician-details' && editingClinicianId === clinician.id ? (
                                  <>
                                    <input
                                      type="text"
                                      list={`suburb-list-${clinician.id}`}
                                      value={editFormData.suburb || ''}
                                      onChange={(e) => updateEditFormData('suburb', e.target.value)}
                                      placeholder="Type or select suburb"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <datalist id={`suburb-list-${clinician.id}`}>
                                      {getUniqueSuburbs.map((suburb) => (
                                        <option key={suburb} value={suburb} />
                                      ))}
                                    </datalist>
                                  </>
                                ) : (
                                  <input
                                    type="text"
                                    value={clinician.suburb || ''}
                                    readOnly
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                  />
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                                {editingSection === 'clinician-details' && editingClinicianId === clinician.id ? (
                                  <>
                                    <input
                                      type="text"
                                      list={`state-list-${clinician.id}`}
                                      value={editFormData.state || ''}
                                      onChange={(e) => updateEditFormData('state', e.target.value)}
                                      placeholder="Type or select state"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <datalist id={`state-list-${clinician.id}`}>
                                      {getUniqueStates.map((state) => (
                                        <option key={state} value={state} />
                                      ))}
                                    </datalist>
                                  </>
                                ) : (
                                  <input
                                    type="text"
                                    value={clinician.state || ''}
                                    readOnly
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                  />
                                )}
                              </div>
              </div>

                            {/* Second Row */}
                            <div className="grid grid-cols-5 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? editFormData.postcode || ''
                                      : clinician.postcode || ''
                                  }
                                  onChange={(e) => editingSection === 'clinician-details' && editingClinicianId === clinician.id && updateEditFormData('postcode', e.target.value)}
                                  readOnly={!(editingSection === 'clinician-details' && editingClinicianId === clinician.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                                {editingSection === 'clinician-details' && editingClinicianId === clinician.id ? (
                                  <>
                                    <input
                                      type="text"
                                      list={`country-list-${clinician.id}`}
                                      value={editFormData.country || ''}
                                      onChange={(e) => updateEditFormData('country', e.target.value)}
                                      placeholder="Type or select country"
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <datalist id={`country-list-${clinician.id}`}>
                                      {getUniqueCountries.map((country) => (
                                        <option key={country} value={country} />
                                      ))}
                                    </datalist>
                                  </>
                                ) : (
                                  <input
                                    type="text"
                                    value={clinician.country || ''}
                                    readOnly
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                  />
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                  type="text"
                                  value={
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? editFormData.phone || ''
                                      : clinician.phone || ''
                                  }
                                  onChange={(e) => editingSection === 'clinician-details' && editingClinicianId === clinician.id && updateEditFormData('phone', e.target.value)}
                                  readOnly={!(editingSection === 'clinician-details' && editingClinicianId === clinician.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                                <input
                                  type="email"
                                  value={
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? editFormData.email || ''
                                      : clinician.email || ''
                                  }
                                  onChange={(e) => editingSection === 'clinician-details' && editingClinicianId === clinician.id && updateEditFormData('email', e.target.value)}
                                  readOnly={!(editingSection === 'clinician-details' && editingClinicianId === clinician.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'clinician-details' && editingClinicianId === clinician.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Assigned Clinic</label>
                                {editingSection === 'clinician-details' && editingClinicianId === clinician.id ? (
                                  <div>

                                    <select
                                      value={String(editFormData.assigned_clinic_id || '')}
                                      onChange={(e) => {
                                        console.log('🏥 Clinic selection changed to:', e.target.value);
                                        console.log('🏥 Available clinics at change:', availableClinics);
                                        // Convert to number if not empty, otherwise keep as empty string
                                        const clinicId = e.target.value ? parseInt(e.target.value) : '';
                                        updateEditFormData('assigned_clinic_id', clinicId);
                                      }}
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="">No clinic assigned</option>
                                      {availableClinics.length > 0 ? (
                                        availableClinics
                                          .filter(clinic => 
                                            clinic.clinic_name?.toLowerCase().includes(clinicSearchTerm.toLowerCase()) ||
                                            clinic.clinic_address?.toLowerCase().includes(clinicSearchTerm.toLowerCase())
                                          )
                                          .map(clinic => (
                                            <option key={clinic.id} value={String(clinic.id)}>
                                              {clinic.clinic_name} - {clinic.suburb}, {clinic.state}
                                            </option>
                                          ))
                                      ) : (
                                        <option disabled>Loading clinics...</option>
                                      )}
                                    </select>
                                  </div>
                                ) : (
                                  <input
                                    type="text"
                                    value={clinician.assigned_clinic ? `${clinician.assigned_clinic.clinic_name} - ${clinician.assigned_clinic.suburb}, ${clinician.assigned_clinic.state}` : 'No clinic assigned'}
                                    readOnly
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50"
                                  />
                                )}
                              </div>
                </div>
                

                          </div>
                        </div>

                        {/* Specialties Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedClinician === clinician.id 
                              ? 'bg-gradient-to-r from-blue-50 to-indigo-100 border-b border-blue-200 text-blue-900'
                              : 'bg-blue-600 text-white'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <Award className="h-3 w-3 mr-2" />
                              Specialties
                            </h3>
                            {editingSection === 'specialties' && editingClinicianId === clinician.id ? (
                              <div className="flex gap-2">
                  <button
                                  onClick={saveChanges}
                                  disabled={saveLoading}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {saveLoading ? 'Saving...' : 'Save'}
                  </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit('specialties', clinician.id)}
                                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                              >
                                Edit Record
                              </button>
                            )}
                          </div>
                          <div className="p-3">
                            <div className="grid grid-cols-7 gap-4">
                              {[
                                { key: 'workforce_medical', label: 'Workforce Medical' },
                                { key: 'general_medical', label: 'General Medical' },
                                { key: 'ecg', label: 'ECG' },
                                { key: 'spirometry', label: 'Spirometry' },
                                { key: 'serology', label: 'Serology' },
                                { key: 'physiotherapy', label: 'Physiotherapy' },
                                { key: 'hearing', label: 'Hearing' }
                              ].map(specialty => (
                                <div key={specialty.key} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={
                                      editingSection === 'specialties' && editingClinicianId === clinician.id
                                        ? editFormData[specialty.key] || false
                                        : clinician[specialty.key] || false
                                    }
                                    onChange={(e) => editingSection === 'specialties' && editingClinicianId === clinician.id && updateEditFormData(specialty.key, e.target.checked)}
                                    disabled={!(editingSection === 'specialties' && editingClinicianId === clinician.id)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                                  />
                                  <label className="text-xs text-gray-700">{specialty.label}</label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* AC Vitals Access Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedClinician === clinician.id 
                              ? 'bg-gradient-to-r from-green-50 to-emerald-100 border-b border-green-200 text-green-900'
                              : 'bg-green-600 text-white'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <Activity className="h-3 w-3 mr-2" />
                              AC Vitals Monitor Access
                            </h3>
                          </div>
                          <div className="p-3">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-700">Access Status:</span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const newStatus = !clinician.ac_vitals_access;
                                        await clinicianService.updateAcVitalsAccess(clinician.id, newStatus, null);
                                        await fetchClinicians();
                                        addToast(`AC Vitals access ${newStatus ? 'granted' : 'revoked'} successfully`, 'success');
                                      } catch (error) {
                                        addToast('Failed to update AC Vitals access', 'error');
                                      }
                                    }}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                                      clinician.ac_vitals_access ? 'bg-green-600' : 'bg-gray-200'
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        clinician.ac_vitals_access ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                  <span className={`text-xs font-medium ${clinician.ac_vitals_access ? 'text-green-600' : 'text-gray-500'}`}>
                                    {clinician.ac_vitals_access ? 'Enabled' : 'Disabled'}
                                  </span>
                                </div>
                              </div>
                              
                              {clinician.ac_vitals_access && (
                                <>
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-gray-700">Access Duration:</span>
                                    <select
                                      value={clinician.ac_vitals_access_expiry ? 'custom' : 'indefinite'}
                                      onChange={async (e) => {
                                        const duration = e.target.value;
                                        let days = null;
                                        if (duration !== 'indefinite') {
                                          days = parseInt(duration);
                                        }
                                        try {
                                          await clinicianService.updateAcVitalsAccess(clinician.id, true, days);
                                          await fetchClinicians();
                                          addToast('Access duration updated successfully', 'success');
                                        } catch (error) {
                                          addToast('Failed to update access duration', 'error');
                                        }
                                      }}
                                      className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                    >
                                      <option value="indefinite">Indefinite</option>
                                      <option value="5">5 days</option>
                                      <option value="15">15 days</option>
                                      <option value="30">30 days</option>
                                      <option value="60">60 days</option>
                                      <option value="90">90 days</option>
                                    </select>
                                  </div>
                                  
                                  {clinician.ac_vitals_access_expiry && (
                                    <div className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">
                                      <span className="font-medium">Expires on:</span> {new Date(clinician.ac_vitals_access_expiry).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Accounts Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedClinician === clinician.id 
                              ? 'bg-gradient-to-r from-blue-50 to-indigo-100 border-b border-blue-200 text-blue-900'
                              : 'bg-blue-600 text-white'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <Settings className="h-3 w-3 mr-2" />
                              Accounts
                            </h3>
                            {editingSection === 'accounts' && editingClinicianId === clinician.id ? (
                              <div className="flex gap-2">
                      <button
                                  onClick={saveChanges}
                                  disabled={saveLoading}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {saveLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit('accounts', clinician.id)}
                                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                              >
                                Edit Record
                              </button>
                            )}
                          </div>
                          <div className="p-3">
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Email</label>
                                <input
                                  type="email"
                                  value={
                                    editingSection === 'accounts' && editingClinicianId === clinician.id
                                      ? editFormData.accounts_email || ''
                                      : clinician.accounts_email || ''
                                  }
                                  onChange={(e) => editingSection === 'accounts' && editingClinicianId === clinician.id && updateEditFormData('accounts_email', e.target.value)}
                                  readOnly={!(editingSection === 'accounts' && editingClinicianId === clinician.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'accounts' && editingClinicianId === clinician.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Hourly Rate</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={
                                    editingSection === 'accounts' && editingClinicianId === clinician.id
                                      ? editFormData.hourly_rate || ''
                                      : clinician.hourly_rate || ''
                                  }
                                  onChange={(e) => editingSection === 'accounts' && editingClinicianId === clinician.id && updateEditFormData('hourly_rate', e.target.value)}
                                  readOnly={!(editingSection === 'accounts' && editingClinicianId === clinician.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'accounts' && editingClinicianId === clinician.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Appt. Rate</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={
                                    editingSection === 'accounts' && editingClinicianId === clinician.id
                                      ? editFormData.appt_rate || ''
                                      : clinician.appt_rate || ''
                                  }
                                  onChange={(e) => editingSection === 'accounts' && editingClinicianId === clinician.id && updateEditFormData('appt_rate', e.target.value)}
                                  readOnly={!(editingSection === 'accounts' && editingClinicianId === clinician.id)}
                                  className={`w-full px-2 py-1.5 text-xs border border-gray-300 rounded ${
                                    editingSection === 'accounts' && editingClinicianId === clinician.id
                                      ? 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                      : 'bg-gray-50'
                                  }`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Qualifications & Insurances Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`px-3 py-1.5 flex justify-between items-center ${
                            expandedClinician === clinician.id 
                              ? 'bg-gradient-to-r from-blue-50 to-indigo-100 border-b border-blue-200 text-blue-900'
                              : 'bg-blue-600 text-white'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <FileText className="h-3 w-3 mr-2" />
                              Qualifications & Insurances
                            </h3>
                            <button
                              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                            >
                              Edit Record
                      </button>
                          </div>
                          <div className="p-3">
                            <div className="grid grid-cols-5 gap-3">
                              {getDummyQualifications(clinician.id).map((qualification) => {
                                const IconComponent = qualification.icon;
                                return (
                                  <div key={qualification.id} className="text-center">
                                    <div className="w-12 h-12 bg-red-100 border-2 border-red-300 rounded flex items-center justify-center mb-2 mx-auto">
                                      <IconComponent className="h-6 w-6 text-red-600" />
                                    </div>
                                    <span className="text-xs text-gray-600 block leading-tight">
                                      {qualification.name}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
                  </div>
                  
      {/* Add Clinician Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Add New Clinician</h2>
                  <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                <X className="h-6 w-6" />
                  </button>
                </div>
            
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleAddClinician(); }}>
                {/* Clinician Details */}
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Clinician Details
                  </h3>
                  
                  {/* First Row */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
                      <input
                        type="text"
                        value={addClinicianData.firstname}
                        onChange={(e) => updateAddClinicianData('firstname', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
              </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Last Name *</label>
                      <input
                        type="text"
                        value={addClinicianData.lastname}
                        onChange={(e) => updateAddClinicianData('lastname', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
            </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        value={addClinicianData.email}
                        onChange={(e) => updateAddClinicianData('email', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
          </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="text"
                        value={addClinicianData.phone}
                        onChange={(e) => updateAddClinicianData('phone', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
        </div>
      </div>

                  {/* Address Row */}
                  <div className="grid grid-cols-5 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                      <input
                        type="text"
                        value={addClinicianData.clinician_address}
                        onChange={(e) => updateAddClinicianData('clinician_address', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Suburb</label>
                      <input
                        type="text"
                        value={addClinicianData.suburb}
                        onChange={(e) => updateAddClinicianData('suburb', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        value={addClinicianData.state}
                        onChange={(e) => updateAddClinicianData('state', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                      <input
                        type="text"
                        value={addClinicianData.postcode}
                        onChange={(e) => updateAddClinicianData('postcode', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                      <input
                        type="text"
                        value={addClinicianData.country}
                        onChange={(e) => updateAddClinicianData('country', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Specialties */}
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <Award className="h-4 w-4 mr-2" />
                    Specialties
                  </h3>
                  <div className="grid grid-cols-7 gap-4">
                    {[
                      { key: 'workforce_medical', label: 'Workforce Medical' },
                      { key: 'general_medical', label: 'General Medical' },
                      { key: 'ecg', label: 'ECG' },
                      { key: 'spirometry', label: 'Spirometry' },
                      { key: 'serology', label: 'Serology' },
                      { key: 'physiotherapy', label: 'Physiotherapy' },
                      { key: 'hearing', label: 'Hearing' }
                    ].map(specialty => (
                      <div key={specialty.key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={addClinicianData[specialty.key] || false}
                          onChange={(e) => updateAddClinicianData(specialty.key, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                        />
                        <label className="text-xs text-gray-700">{specialty.label}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Accounts */}
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <Settings className="h-4 w-4 mr-2" />
                    Accounts
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Accounts Email</label>
                      <input
                        type="email"
                        value={addClinicianData.accounts_email}
                        onChange={(e) => updateAddClinicianData('accounts_email', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Hourly Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        value={addClinicianData.hourly_rate}
                        onChange={(e) => updateAddClinicianData('hourly_rate', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Appt. Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        value={addClinicianData.appt_rate}
                        onChange={(e) => updateAddClinicianData('appt_rate', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addLoading || !addClinicianData.firstname || !addClinicianData.lastname || !addClinicianData.email}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addLoading ? 'Creating...' : 'Create Clinician'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCliniciansPage;